/**
 * localStorageの1つのキーを、画面のどこからでも同じ値で読めるようにする。useSyncExternalStore
 * に渡して使う。保存値はparseで確かめ、ほかのタブでの変更も届ける。
 */
export function createLocalStorageStore<T>({
  key,
  parse,
  onChange,
}: {
  key: string;
  /** 保存値を確かめる。保存値がないときはnullを受け取り、既定値を返す。 */
  parse: (raw: string | null) => T;
  /** 値が変わったときに呼ぶ。このタブで保存したときと、ほかのタブで変えたときの両方。 */
  onChange?: (value: T) => void;
}) {
  const fallback = parse(null);
  const listeners = new Set<() => void>();
  let cached: { raw: string | null; value: T } = { raw: null, value: fallback };

  const read = (): T => {
    let raw: string | null = null;
    // 保存領域を使えないブラウザーでは既定値にする。
    try {
      raw = window.localStorage.getItem(key);
    } catch {}
    if (cached.raw !== raw) cached = { raw, value: parse(raw) };
    return cached.value;
  };

  const changed = () => {
    onChange?.(read());
    for (const listener of listeners) listener();
  };

  // localStorage.clear()ではkeyがnullになる。
  const handleStorage = (event: StorageEvent) => {
    if (event.key === key || event.key === null) changed();
  };

  return {
    read,
    readServer: () => fallback,
    subscribe(listener: () => void) {
      if (listeners.size === 0) {
        window.addEventListener("storage", handleStorage);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          window.removeEventListener("storage", handleStorage);
        }
      };
    },
    /** 保存して知らせる。容量を超えたときなどは、localStorageの例外をそのまま投げる。 */
    save(value: T) {
      window.localStorage.setItem(key, JSON.stringify(value));
      changed();
    },
  };
}
